import { inject } from '@angular/core'

import { isObjectLike } from 'lodash-es'
import { Observable } from 'rxjs'

import {
  AuditAction,
  AuditCategory,
  AuditLog,
  DataQueryProps,
  DeviceCamera,
  LocationType,
  UserProfile,
} from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { isCustomRole } from '@ti/core/auth/utils/is-custom-role'
import { ExportFormat } from '@ti/core/common/contracts'
import { UnitType } from '@ti/core/contracts/i18n'
import {
  DateFormatService,
  LanguageService,
  TimezoneOptionsProvider,
  UnitFormattingService,
} from '@ti/core/ui-kit/i18n'

import { ActionData } from '../contracts'
import { formatAddress, formatVehicleInfo } from '../utils'

const EMPTY_PLACEHOLDER = '---'

/**
 * Need to specify all variables there to handle missing values
 */
const AUDIT_LOG_ACTION_VARIABLES = [
  'alertName',
  'alertSubTrigger',
  'alertTrigger',
  'alertLabel',
  'app',
  'bookmarkName',
  'command',
  'commandName',
  'comment',
  'customSensorDataType',
  'customSensorName',
  'deviceId',
  'driverName',
  'effectiveDate',
  'endDate',
  'executionStatus',
  'fleetAccountName',
  'fleetName',
  'flespiResponseText',
  'incidentName',
  'issuesCount',
  'isSuccess',
  'linkName',
  'listCameras',
  'limit',
  'locationType',
  'mediaTitle',
  'name',
  'newAddress',
  'newValue',
  'numberOfSeconds',
  'oldAddress',
  'oldValue',
  'performedServiceName',
  'recipients',
  'reportName',
  'roleName',
  'scheduleName',
  'scheduledServiceName',
  'serviceName',
  'startDate',
  'templateName',
  'statusAfter',
  'statusBefore',
  'tspId',
  'userEmail',
  'userName',
  'userRole',
  'vehicleName',
  'webhookName',
  'widgetName',
]

const AUDIT_LOG_ACTION_VARIABLE_DEFAULTS = AUDIT_LOG_ACTION_VARIABLES.reduce(
  (variables, key) => Object.assign(variables, { [key]: EMPTY_PLACEHOLDER }),
  {} as ActionData,
)

const AUDIT_LOG_ACTION_CATEGORY_OVERRIDES: Partial<Record<AuditAction, string>> = {
  [AuditAction.ApiPerMinuteLimitExceeded]: 'rate-limit',
  [AuditAction.ApiDailyLimitExceeded]: 'rate-limit',
}

export abstract class AuditLogGridDataResolver {
  public abstract SUPPORTED_CATEGORIES: AuditCategory[]

  protected readonly api = inject(ApiService)
  protected readonly timezoneOptions = inject(TimezoneOptionsProvider)
  protected readonly languageService = inject(LanguageService)
  protected readonly dateFormatService = inject(DateFormatService)
  protected readonly unitFormattingService = inject(UnitFormattingService)

  public async getActionKey(row: AuditLog) {
    let action = row.action as string

    // Use different string for Users when `userRoleId` is specified
    if (
      row.category.endsWith(AuditCategory.Users) &&
      (row.action === AuditAction.UserInvited || row.action === AuditAction.Deleted) &&
      typeof row.data?.userRoleId === 'number'
    ) {
      action = `${row.action}-v2`
    }

    if (row.action === AuditAction.InspectionReportSubmitted) {
      action = `${action}-${Number(row.data?.issuesCount) > 0 ? 'with' : 'without'}-issues`
    }

    const actionCategory = this.getActionTranslationCategory(row.category, action)

    // Allow third level key with updated field name
    if (row?.data?.field && row.action === AuditAction.FieldChanged) {
      return `audit-log.actions.${actionCategory}.${action}.${row.data.field}`
    }

    return `audit-log.actions.${actionCategory}.${action}`
  }

  protected getActionTranslationCategory(category: AuditCategory, action: string): string {
    return AUDIT_LOG_ACTION_CATEGORY_OVERRIDES[action as AuditAction] ?? category
  }

  public async getActionData(row: AuditLog): Promise<ActionData> {
    // Assign default value for row data
    row.data = row.data ?? {}

    // Add defaults for all supported variables
    const actionData: ActionData = Object.assign(
      {},
      AUDIT_LOG_ACTION_VARIABLE_DEFAULTS,
      row.data ?? {},
    )

    if (row.data.deviceIdentifier) {
      actionData.deviceId = row.data.deviceIdentifier
    }

    if (row.category === AuditCategory.Bookmarks) {
      if (!row.data?.bookmarkName && (row.entityName || row.data?.nameBefore)) {
        actionData.bookmarkName = row.entityName || row.data?.nameBefore
      }
    }

    if (
      row.category === AuditCategory.CommandsAlertsBased ||
      row.category === AuditCategory.CommandsManual
    ) {
      actionData.isSuccess =
        row.data.isSuccessful !== undefined ? Boolean(row.data.isSuccessful).toString() : ''

      actionData.flespiResponseText = row.data?.response?.result?.response ?? ''

      if (!actionData.flespiResponseText && row.data?.response?.error) {
        const error = row.data?.response?.error
        const errorLabel = this.languageService.instant('audit-log.error')

        if (typeof error === 'string') {
          actionData.flespiResponseText = `${errorLabel} '${error}'`
        } else if (
          typeof error === 'object' &&
          error?.reason &&
          typeof error?.reason === 'string'
        ) {
          actionData.flespiResponseText = `${errorLabel} '${error?.reason}'`
        } else {
          actionData.flespiResponseText = `${errorLabel} '${error.toString()}'`
        }
      }
    }

    if (row.category === AuditCategory.Webhook) {
      actionData.webhookName = row.data.webhookName
      actionData.alertLabel = this.formatWebhookAlertLabel(row.data)
      actionData.fleetName = row.data.fleetName

      const executionStatusKey = this.getWebhookExecutionStatusKey(row.data.isExecutionSuccessful)
      actionData.executionStatus = executionStatusKey
        ? await this.languageService.translate(`audit-log.execution-status.${executionStatusKey}`)
        : ''
    }

    if (row.category === AuditCategory.Locations) {
      switch (row.data?.locationType) {
        case LocationType.Address:
          actionData.locationType = this.languageService.instant(
            'locations.filter.type.option.address',
          )
          break
        case LocationType.Circle:
          actionData.locationType = this.languageService.instant(
            'locations.filter.type.option.circle',
          )
          break
        case LocationType.Polygon:
          actionData.locationType = this.languageService.instant(
            'locations.filter.type.option.polygon',
          )
          break
        case LocationType.Corridor:
          actionData.locationType = this.languageService.instant(
            'locations.filter.type.option.corridor',
          )
      }
    }

    if (row.category === AuditCategory.Media) {
      if (row.data?.vehicleName && row.data?.mediaStartDate) {
        const dateTimeText = this.dateFormatService.formatDateInstant(
          row.data.mediaStartDate,
          'grid-date-time',
        )
        actionData.mediaTitle = `${row.data.vehicleName} ${dateTimeText}`
      }

      if (Array.isArray(row?.data?.cameras) && Array.isArray(row?.data?.channels)) {
        actionData.listCameras = row.data.channels
          .map(
            (channelId) =>
              row.data.cameras.find((camera: DeviceCamera) => camera.channel === channelId)?.name,
          )
          .filter(Boolean)
          .join(', ')
      }

      if (row?.data?.durationSec !== undefined) {
        actionData.numberOfSeconds = `${Math.round(row.data.durationSec)}`
      }
    }

    if (row.category === AuditCategory.Settings) {
      actionData.oldAddress = formatAddress(row.data?.oldValue)
      actionData.newAddress = formatAddress(row.data?.newValue)
    }

    if (row.category === AuditCategory.Users && row.action === AuditAction.Modified) {
      if (isObjectLike(row?.data?.oldValue)) {
        actionData.userName = row.entityName || row.data.oldValue.name
        actionData.userEmail = row.data.userEmail || row.data.oldValue.email
      } else {
        actionData.userName = row.entityName
      }
    }

    if (row.category === AuditCategory.Vehicles) {
      if (row.entityName) {
        actionData.vehicleName = row.entityName
      }

      if (row.action === AuditAction.FieldChanged && row.data?.field === 'currentMileage') {
        const options = {
          addLabel: true,
          precision: 0,
          commaSeparated: true,
          usePreciseIfLess: null,
        }
        const [oldValue, newValue] = await Promise.all([
          this.unitFormattingService.getUnitText(row.data?.oldValue, UnitType.Distance, options),
          this.unitFormattingService.getUnitText(row.data?.newValue, UnitType.Distance, options),
        ])
        actionData.oldValue = oldValue
        actionData.newValue = newValue
      }

      if (
        row.action === AuditAction.FieldChanged &&
        row.data?.field === 'year/make/model/license'
      ) {
        actionData.oldValue = formatVehicleInfo(row.data.oldValue)
        actionData.newValue = formatVehicleInfo(row.data.newValue)
      }
    }

    if (row.category === AuditCategory.Drivers) {
      if (
        row.action === AuditAction.DriverLinked ||
        row.action === AuditAction.DriverUnlinked ||
        row.action === AuditAction.RFIDScanDriverLinked
      ) {
        actionData.effectiveDate = this.dateFormatService.formatDateInstant(
          row.data?.effectiveDate ?? row.actionDate,
          'grid-date-time',
        )
      }

      if (
        row.action === AuditAction.DriverLinkCreated ||
        row.action === AuditAction.DriverLinkDeleted
      ) {
        actionData.startDate = row.data?.assignedAt
          ? this.dateFormatService.formatDateInstant(row.data.assignedAt, 'grid-date-time')
          : EMPTY_PLACEHOLDER
        actionData.endDate = row.data?.unassignedAt
          ? this.dateFormatService.formatDateInstant(row.data.unassignedAt, 'grid-date-time')
          : EMPTY_PLACEHOLDER
      }
    }

    if (row.action === AuditAction.FieldChanged && row.data?.field === 'timezone') {
      actionData.oldValue = await this.languageService.translate(
        this.timezoneOptions.getTimezoneTranslationLabel(row.data?.oldValue),
      )
      actionData.newValue = await this.languageService.translate(
        this.timezoneOptions.getTimezoneTranslationLabel(row.data?.newValue),
      )
    }

    if (
      row.action === AuditAction.FieldChanged &&
      row.data?.field === 'driverScoreWeights' &&
      row.data?.incident
    ) {
      actionData.incidentName = await this.languageService.translate(
        `fleet-settings.driving-score.incident.${row.data.incident}`,
      )
    }

    if (row.action === AuditAction.SentToDriver && row.data?.comment) {
      const comment = await this.languageService.translate(
        'audit-log.actions.alerts.with-comment',
        {
          comment: row.data?.comment,
        },
      )
      actionData.comment = `. ${comment}`
    }

    if (row.category === AuditCategory.Dashboard) {
      actionData.widgetName = await this.languageService.translate(
        `dashboard.widgets.${row.data.widgetKey}.title`,
      )
    }

    if (row.category === AuditCategory.Users) {
      if (typeof row.data.userRoleId === 'number') {
        actionData.userRole = await this.languageService.translate(
          `users.roles.${row.data.userRoleId}`,
        )
      }

      if (row.action === AuditAction.FieldChanged && row.data?.field === 'roleId') {
        actionData.oldValue = row.data.oldValue
          ? isCustomRole(row.data.oldValue)
            ? row.data.oldRoleName || '---'
            : await this.languageService.translate(`users.roles.${row.data.oldValue}`)
          : EMPTY_PLACEHOLDER
        actionData.newValue = row.data.newValue
          ? isCustomRole(row.data.newValue)
            ? row.data.newRoleName || '---'
            : await this.languageService.translate(`users.roles.${row.data.newValue}`)
          : EMPTY_PLACEHOLDER
      }
    }

    if (row.category === AuditCategory.CustomSensors && row.data.customSensorDataType) {
      actionData.customSensorDataType = await this.languageService.translate(
        `tsp-admin.devices.custom-sensors.data-type.${row.data.customSensorDataType}`,
      )
    }

    if (row.category === AuditCategory.ShareableLocationLinks) {
      actionData.linkName = row.data.displayName || EMPTY_PLACEHOLDER
    }

    return actionData
  }

  protected formatWebhookAlertLabel(data: Record<string, any>): string {
    const alertLabel = data.alertIsDefaultName
      ? data.alertName
      : `${data.alertName} (${data.alertSubTrigger})`

    return `${alertLabel} - ${data.alertTrigger}`
  }

  protected getWebhookExecutionStatusKey(value: unknown): 'successfully' | 'unsuccessfully' | null {
    if (value === true || value === 'true') {
      return 'successfully'
    }

    if (value === false || value === 'false') {
      return 'unsuccessfully'
    }

    return null
  }

  public getSystemUserFilterValue(): string | null {
    return null
  }

  public async loadPage(props: DataQueryProps): Promise<AuditLog[]> {
    return this.api.auditLogs.list(props)
  }

  // TODO: Implement users store and remove this method
  public abstract getAllUsers(): Promise<UserProfile[]>

  public getGridTranslationKeys() {
    return {
      userName: 'audit-log.grid.user',
      action: 'audit-log.grid.action',
      actionDate: 'audit-log.grid.date-and-time',
      category: 'audit-log.grid.category',
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public export$(format: ExportFormat, fileName: string, params: DataQueryProps): Observable<void> {
    return new Observable(() => {})
  }
}
